import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { detectPageCorners, PAGE_DETECT_VERSION } from "./invoiceEdgeDetect";
import {
  blobToFile,
  defaultCorners,
  imageDataToJpegBlob,
  isInvoiceImageFile,
  isInvoicePdfFile,
  loadImageFromFile,
  rotateImageDataQuarterTurns,
  rotateImageFile,
  warpPerspectiveToImageData,
} from "./invoicePerspective";
import { ENHANCE_VERSION, enhanceDocumentImageData } from "./invoiceEnhance";
import { pickInvoiceUploadFile } from "./invoiceFilePick";

const HANDLE_LABELS = ["TL", "TR", "BR", "BL"];
/**
 * Edge endpoints in TL/TR/BR/BL order.
 * axis: "y" = top/bottom (translate vertically only);
 *       "x" = left/right (translate horizontally only).
 */
const EDGE_PAIRS = [
  { i0: 0, i1: 1, axis: "y" }, // top
  { i0: 1, i1: 2, axis: "x" }, // right
  { i0: 2, i1: 3, axis: "y" }, // bottom
  { i0: 3, i1: 0, axis: "x" }, // left
];
const LOUPE_SIZE = 132;
/** Lower zoom = more context around the drop point (easier corner placement). */
const LOUPE_ZOOM = 1.85;
const LOUPE_GAP = 40;

/** Stage height that fits both portrait and landscape — rotate does not resize. */
function lockedPreviewHeightPx(imgW, imgH, boxW, maxH) {
  if (!imgW || !imgH || boxW <= 0 || maxH <= 0) return Math.ceil(maxH || 0);
  const fitH = (w, h) => {
    const s = Math.min(boxW / w, maxH / h);
    return h * s;
  };
  return Math.ceil(Math.max(fitH(imgW, imgH), fitH(imgH, imgW)));
}

function clientToNatural(clientX, clientY, img, layout, natural) {
  const rect = img.getBoundingClientRect();
  const localX = clientX - rect.left - layout.offsetX;
  const localY = clientY - rect.top - layout.offsetY;
  return {
    x: Math.min(natural.w, Math.max(0, localX / layout.scale)),
    y: Math.min(natural.h, Math.max(0, localY / layout.scale)),
  };
}

function clampPoint(p, w, h) {
  return {
    x: Math.min(w, Math.max(0, p.x)),
    y: Math.min(h, Math.max(0, p.y)),
  };
}

/**
 * 4-corner perspective crop for invoice photos.
 * Drag corner anchors or whole edges (both endpoints move in parallel),
 * then straighten. Rotate 90° for sideways Android camera shots.
 * Magnifier loupe while dragging so the drop point is precise.
 */
export function InvoicePerspectiveCrop({
  file,
  t,
  onCancel,
  onConfirm,
}) {
  const stageRef = useRef(null);
  const imgRef = useRef(null);
  const loupeRef = useRef(null);
  const resultStageRef = useRef(null);
  const [workingFile, setWorkingFile] = useState(file);
  const [objectUrl, setObjectUrl] = useState(null);
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [corners, setCorners] = useState(null);
  const [layout, setLayout] = useState(null);
  /**
   * Active drag:
   *   { type: "corner", index }
   *   { type: "edge", i0, i1, axis: "x"|"y", origin: {x,y}, base: [{x,y}, {x,y}] }
   * Edge drags store the pointer start + both corner snapshots. Top/bottom
   * (axis "y") move vertically only; left/right (axis "x") horizontally only.
   * Both ends share the same delta so the side stays parallel.
   */
  const [drag, setDrag] = useState(null);
  /** Touch/finger drag → loupe always above; mouse → under for top corners. */
  const [dragIsTouch, setDragIsTouch] = useState(false);
  const [busy, setBusy] = useState(false);
  const [detectBusy, setDetectBusy] = useState(false);
  const [error, setError] = useState(null);
  const [detectNote, setDetectNote] = useState(null);
  /** Straightened output awaiting the user's OK: { file, url } or null. */
  const [result, setResult] = useState(null);
  /**
   * [Fable 5.1 · Scan-look enhancer] invariant 7 — see invoiceEnhance.js.
   * Scan-look filter (white paper, crisp ink — CamScanner style). Default ON;
   * the toggle and rotate re-encode from `warpedRef`, never a second warp.
   */
  const [enhance, setEnhance] = useState(true);
  /** Extra quarter turns the user applied on the result preview. */
  const [resultTurns, setResultTurns] = useState(0);
  /**
   * After Add Page: keep this dialog open and show a dropzone for the next
   * photo instead of dismissing to a native file picker.
   */
  const [awaitingNextPage, setAwaitingNextPage] = useState(false);
  const [nextDragOver, setNextDragOver] = useState(false);
  /**
   * Saved pages in this crop session (thumbnails / reorder / re-edit).
   * Committed to the parent only on Use this / Done — not on each Add Page.
   * { id, file, meta, originalFile, previewUrl }
   */
  const [draftPages, setDraftPages] = useState([]);
  /** When re-editing a draft page, its index; null = new page. */
  const [editingIndex, setEditingIndex] = useState(null);
  /** Camera/import file for the page currently being straightened (training). */
  const importOriginalRef = useRef(file);
  /**
   * Fixed stage height = max(portrait fit, landscape fit). Shared by corners,
   * result, and dropzone so Rotate never resizes the dialog.
   */
  const [stageFrameH, setStageFrameH] = useState(null);
  const stageFrameLockedRef = useRef(null);
  /** Raw warped RGBA (pre-rotate, pre-enhance) so toggles are cheap. */
  const warpedRef = useRef(null);
  const imageRef = useRef(null);
  /** Keep previous object URL until the next decode finishes (no blank flash on rotate). */
  const objectUrlRef = useRef(null);
  const draftPagesRef = useRef(draftPages);
  draftPagesRef.current = draftPages;
  const editingIndexRef = useRef(editingIndex);
  editingIndexRef.current = editingIndex;
  const cornersRef = useRef(corners);
  cornersRef.current = corners;
  const enhanceRef = useRef(enhance);
  enhanceRef.current = enhance;
  const naturalRef = useRef(natural);
  naturalRef.current = natural;

  useEffect(() => {
    if (!result) return undefined;
    const { url, owned } = result;
    return () => {
      if (owned && url) URL.revokeObjectURL(url);
    };
  }, [result]);

  useEffect(() => {
    return () => {
      for (const p of draftPagesRef.current) {
        if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
      }
    };
  }, []);

  useEffect(() => {
    setWorkingFile(file);
    importOriginalRef.current = file;
    setAwaitingNextPage(false);
    setEditingIndex(null);
    setResult(null);
    stageFrameLockedRef.current = null;
    setStageFrameH(null);
    // New import only — rotate keeps the previous bitmap via workingFile.
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setObjectUrl(null);
    setNatural({ w: 0, h: 0 });
    setCorners(null);
    setLayout(null);
    setDetectNote(null);
    imageRef.current = null;
    setDraftPages((prev) => {
      for (const p of prev) {
        if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
      }
      return [];
    });
  }, [file]);

  useLayoutEffect(() => {
    if (!natural.w || !natural.h) return undefined;
    const compute = () => {
      const boxW =
        stageRef.current?.clientWidth ||
        resultStageRef.current?.clientWidth ||
        Math.min(typeof window !== "undefined" ? window.innerWidth * 0.96 : 880, 880) - 140;
      const maxH =
        typeof window !== "undefined" ? Math.min(window.innerHeight * 0.72, 720) : 720;
      return lockedPreviewHeightPx(natural.w, natural.h, boxW, maxH);
    };
    // Lock once per page session so rotate never grows/shrinks the stage.
    // Window resize may recompute; natural w/h swaps from rotate must not.
    if (stageFrameLockedRef.current == null) {
      const h = compute();
      stageFrameLockedRef.current = h;
      setStageFrameH(h);
    }
    const onResize = () => {
      const h = compute();
      stageFrameLockedRef.current = h;
      setStageFrameH(h);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [natural.w, natural.h]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    // Keep the previous bitmap + corners on screen until the new decode is
    // ready — clearing them first made the stage flash empty on every rotate.
    loadImageFromFile(workingFile)
      .then(({ image, objectUrl: url }) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        const prevUrl = objectUrlRef.current;
        objectUrlRef.current = url;
        imageRef.current = image;
        const nw = image.naturalWidth;
        const nh = image.naturalHeight;
        const fallback = defaultCorners(nw, nh);
        let detected = null;
        try {
          detected = detectPageCorners(image);
        } catch {
          detected = null;
        }
        const nextCorners =
          detected && detected.length === 4 ? detected : fallback;
        const nextNote =
          detected && detected.length === 4 ? "auto" : "fallback";
        setObjectUrl(url);
        setNatural({ w: nw, h: nh });
        setCorners(nextCorners);
        setDetectNote(nextNote);
        if (prevUrl && prevUrl !== url) URL.revokeObjectURL(prevUrl);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Could not open image");
      });
    return () => {
      cancelled = true;
    };
  }, [workingFile]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, []);

  function handleDetectEdges() {
    const image = imageRef.current;
    if (!image || detectBusy || busy) return;
    setDetectBusy(true);
    setError(null);
    // Yield so the button can show busy state on slow phones.
    window.setTimeout(() => {
      try {
        const detected = detectPageCorners(image);
        if (detected && detected.length === 4) {
          setCorners(detected);
          setDetectNote("auto");
        } else {
          setDetectNote("miss");
          setError(
            t(
              "Could not find page edges — drag the corners manually.",
              "Δεν βρέθηκαν άκρες σελίδας — σύρετε τις γωνίες χειροκίνητα."
            )
          );
        }
      } catch (err) {
        setDetectNote("miss");
        setError(err?.message || "Detect failed");
      } finally {
        setDetectBusy(false);
      }
    }, 30);
  }

  const measure = useCallback(() => {
    const img = imgRef.current;
    if (!img || !natural.w) return;
    const rect = img.getBoundingClientRect();
    const scale = Math.min(rect.width / natural.w, rect.height / natural.h);
    const dispW = natural.w * scale;
    const dispH = natural.h * scale;
    const offsetX = (rect.width - dispW) / 2;
    const offsetY = (rect.height - dispH) / 2;
    setLayout({
      scale,
      offsetX,
      offsetY,
      dispW,
      dispH,
      left: rect.left,
      top: rect.top,
    });
  }, [natural.w, natural.h]);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure, objectUrl]);

  useEffect(() => {
    if (!drag) return undefined;
    const onMove = (e) => {
      if (e.cancelable) e.preventDefault();
      if (!layout) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const img = imgRef.current;
      if (!img) return;
      const at = clientToNatural(clientX, clientY, img, layout, natural);
      if (drag.type === "corner") {
        setCorners((prev) => {
          if (!prev) return prev;
          const next = prev.map((c) => ({ ...c }));
          next[drag.index] = at;
          return next;
        });
        return;
      }
      // Edge: translate both endpoints along one axis only (top/bottom → y,
      // left/right → x). Scale the delta if either end would leave the frame
      // so the side stays parallel (independent clamps would skew it).
      let dx = drag.axis === "x" ? at.x - drag.origin.x : 0;
      let dy = drag.axis === "y" ? at.y - drag.origin.y : 0;
      let s = 1;
      for (const base of drag.base) {
        if (dx > 0) s = Math.min(s, (natural.w - base.x) / dx);
        else if (dx < 0) s = Math.min(s, -base.x / dx);
        if (dy > 0) s = Math.min(s, (natural.h - base.y) / dy);
        else if (dy < 0) s = Math.min(s, -base.y / dy);
      }
      s = Math.max(0, Number.isFinite(s) ? s : 0);
      dx *= s;
      dy *= s;
      setCorners((prev) => {
        if (!prev) return prev;
        const next = prev.map((c) => ({ ...c }));
        next[drag.i0] = clampPoint(
          { x: drag.base[0].x + dx, y: drag.base[0].y + dy },
          natural.w,
          natural.h
        );
        next[drag.i1] = clampPoint(
          { x: drag.base[1].x + dx, y: drag.base[1].y + dy },
          natural.w,
          natural.h
        );
        return next;
      });
    };
    const onUp = () => {
      setDrag(null);
      setDragIsTouch(false);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [drag, layout, natural]);

  // Paint magnifier centered on the active drop point (natural image pixels).
  useLayoutEffect(() => {
    if (!drag || !corners || !natural.w) return;
    const canvas = loupeRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !img.complete) return;
    let focus;
    if (drag.type === "corner") {
      focus = corners[drag.index];
    } else {
      const a = corners[drag.i0];
      const b = corners[drag.i1];
      if (!a || !b) return;
      focus = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    }
    if (!focus) return;

    const size = LOUPE_SIZE;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(size * dpr);
    canvas.height = Math.round(size * dpr);
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size, size);

    const srcSize = size / LOUPE_ZOOM;
    const sx = focus.x - srcSize / 2;
    const sy = focus.y - srcSize / 2;

    ctx.save();
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = "#111";
    ctx.fillRect(0, 0, size, size);
    try {
      ctx.drawImage(img, sx, sy, srcSize, srcSize, 0, 0, size, size);
    } catch {
      /* cross-origin / decode edge — leave dark */
    }

    // Crosshair at exact drop point
    ctx.strokeStyle = "rgba(255,255,255,0.95)";
    ctx.lineWidth = 1.25;
    ctx.beginPath();
    ctx.moveTo(size / 2, 10);
    ctx.lineTo(size / 2, size - 10);
    ctx.moveTo(10, size / 2);
    ctx.lineTo(size - 10, size / 2);
    ctx.stroke();
    ctx.strokeStyle = "#2D6A4F";
    ctx.lineWidth = 1.25;
    ctx.beginPath();
    ctx.moveTo(size / 2, 10);
    ctx.lineTo(size / 2, size - 10);
    ctx.moveTo(10, size / 2);
    ctx.lineTo(size - 10, size / 2);
    ctx.stroke();
    ctx.fillStyle = "#2D6A4F";
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, 3.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.restore();

    ctx.strokeStyle = "#2D6A4F";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2);
    ctx.stroke();
    ctx.strokeStyle = "rgba(255,255,255,0.85)";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 4, 0, Math.PI * 2);
    ctx.stroke();
  }, [drag, corners, natural.w, natural.h, objectUrl]);

  async function handleRotate(quarterTurns) {
    if (busy || !workingFile) return;
    setBusy(true);
    setError(null);
    try {
      const rotated = await rotateImageFile(workingFile, quarterTurns);
      setWorkingFile(rotated);
    } catch (err) {
      setError(err?.message || "Rotate failed");
    } finally {
      setBusy(false);
    }
  }

  /**
   * Straighten → result preview. The warp auto-rotates the sheet so text
   * reads upright; the preview lets the user fix a wrong guess with one
   * Rotate click before the file goes to extraction.
   */
  /** Encode the cached warp with the current rotation + enhance choice. */
  async function renderResult({ turns = resultTurns, withEnhance = enhance } = {}) {
    const raw = warpedRef.current;
    if (!raw) return;
    let img = raw;
    const q = ((turns % 4) + 4) % 4;
    if (q) img = rotateImageDataQuarterTurns(img, q);
    if (withEnhance) img = enhanceDocumentImageData(img);
    const blob = await imageDataToJpegBlob(img);
    const out = blobToFile(blob, workingFile.name, {
      suffix: withEnhance ? "scan" : "straight",
    });
    const nextUrl = URL.createObjectURL(out);
    // Swap in one update so the stage never briefly has no result (size flick).
    setResult({ file: out, url: nextUrl, owned: true });
    // Show / refresh this page in the sidebar immediately (not only on Use / Add).
    syncResultIntoSidebar(out, withEnhance);
  }

  function syncResultIntoSidebar(resultFile, withEnhance = enhanceRef.current) {
    if (!resultFile) return;
    const c = cornersRef.current;
    const nat = naturalRef.current;
    const meta = {
      originalFile: importOriginalRef.current || file,
      corners: c ? c.map((pt) => ({ x: pt.x, y: pt.y })) : null,
      naturalWidth: nat.w,
      naturalHeight: nat.h,
      pageDetectVersion: PAGE_DETECT_VERSION,
      enhanced: Boolean(withEnhance),
      enhanceVersion: withEnhance ? ENHANCE_VERSION : null,
      skipped: false,
    };
    const previewUrl = URL.createObjectURL(resultFile);
    const idx = editingIndexRef.current;
    setDraftPages((prev) => {
      if (idx != null && idx >= 0 && idx < prev.length) {
        const old = prev[idx];
        if (old.previewUrl) URL.revokeObjectURL(old.previewUrl);
        const entry = {
          ...old,
          file: resultFile,
          meta,
          originalFile: meta.originalFile || null,
          previewUrl,
        };
        return prev.map((p, i) => (i === idx ? entry : p));
      }
      const entry = {
        id: `page-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file: resultFile,
        meta,
        originalFile: meta.originalFile || null,
        previewUrl,
      };
      const nextIdx = prev.length;
      editingIndexRef.current = nextIdx;
      setEditingIndex(nextIdx);
      return [...prev, entry];
    });
  }

  async function handleStraighten() {
    if (!corners || busy) return;
    setBusy(true);
    setError(null);
    let warpUrl = null;
    try {
      const loaded = await loadImageFromFile(workingFile);
      warpUrl = loaded.objectUrl;
      warpedRef.current = warpPerspectiveToImageData(loaded.image, corners);
      setResultTurns(0);
      await renderResult({ turns: 0 });
    } catch (err) {
      setError(err?.message || "Straighten failed");
    } finally {
      if (warpUrl) URL.revokeObjectURL(warpUrl);
      setBusy(false);
    }
  }

  async function handleRotateResult(quarterTurns) {
    if (!result || busy) return;
    setBusy(true);
    setError(null);
    try {
      const next = (((resultTurns + quarterTurns) % 4) + 4) % 4;
      setResultTurns(next);
      await renderResult({ turns: next });
    } catch (err) {
      setError(err?.message || "Rotate failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleToggleEnhance() {
    if (!result || busy) return;
    const next = !enhance;
    setEnhance(next);
    setBusy(true);
    setError(null);
    try {
      await renderResult({ withEnhance: next });
    } catch (err) {
      setError(err?.message || "Enhance failed");
    } finally {
      setBusy(false);
    }
  }

  function currentPageMeta() {
    return {
      originalFile: importOriginalRef.current || file,
      corners: corners ? corners.map((c) => ({ x: c.x, y: c.y })) : null,
      naturalWidth: natural.w,
      naturalHeight: natural.h,
      pageDetectVersion: PAGE_DETECT_VERSION,
      enhanced: enhance,
      enhanceVersion: enhance ? ENHANCE_VERSION : null,
      skipped: false,
    };
  }

  function makeDraftEntry(resultFile, meta) {
    return {
      id: `page-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file: resultFile,
      meta,
      originalFile: meta.originalFile || null,
      previewUrl: URL.createObjectURL(resultFile),
    };
  }

  /** Persist the current result into draftPages (append or replace edit). */
  function saveResultToDrafts() {
    if (!result?.file) return null;
    // Re-opened draft without a new straighten → keep previous training meta.
    const meta =
      editingIndex != null &&
      !result.owned &&
      draftPages[editingIndex]?.meta
        ? draftPages[editingIndex].meta
        : currentPageMeta();
    const entry = makeDraftEntry(result.file, meta);
    let nextPages;
    if (editingIndex != null && editingIndex >= 0 && editingIndex < draftPages.length) {
      const prev = draftPages[editingIndex];
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      nextPages = draftPages.map((p, i) => (i === editingIndex ? entry : p));
    } else {
      nextPages = [...draftPages, entry];
    }
    setDraftPages(nextPages);
    return nextPages;
  }

  function commitPages(pages) {
    const list = (pages || [])
      .filter((p) => p?.file)
      .map((p) => ({ file: p.file, meta: p.meta || {} }));
    onConfirm(list);
  }

  function handleUseResult({ addAnother = false } = {}) {
    if (!result?.file) return;
    // Fresh straighten needs corners; re-opened draft preview does not.
    if (result.owned && !corners) return;
    if (addAnother) {
      saveResultToDrafts();
      setEditingIndex(null);
      setResult(null);
      setResultTurns(0);
      setError(null);
      warpedRef.current = null;
      setAwaitingNextPage(true);
      return;
    }
    const pages = saveResultToDrafts() || draftPages;
    commitPages(pages);
  }

  function acceptNextPageFile(next) {
    if (!next || busy) return;
    const okPdf = isInvoicePdfFile(next);
    const okImage = isInvoiceImageFile(next);
    if (!okPdf && !okImage) {
      setError(
        t("Use a PDF or photo (JPEG/PNG/WebP)", "Χρησιμοποιήστε PDF ή φωτο (JPEG/PNG/WebP)")
      );
      return;
    }
    setError(null);
    setNextDragOver(false);
    if (okImage && !okPdf) {
      importOriginalRef.current = next;
      setEditingIndex(null);
      setAwaitingNextPage(false);
      stageFrameLockedRef.current = null;
      setStageFrameH(null);
      setWorkingFile(next);
      setResult(null);
      setResultTurns(0);
      setEnhance(true);
      warpedRef.current = null;
      return;
    }
    // PDF: append as a page (no straighten) and commit everything.
    const pdfEntry = {
      id: `page-${Date.now()}-pdf`,
      file: next,
      meta: { skipped: true, originalFile: next },
      originalFile: next,
      previewUrl: null,
    };
    commitPages([...draftPages, pdfEntry]);
  }

  function finishWithoutMorePages() {
    commitPages(draftPages);
  }

  function selectDraftPage(idx) {
    const page = draftPages[idx];
    if (!page || busy) return;
    setAwaitingNextPage(false);
    setEditingIndex(idx);
    setError(null);
    setResultTurns(0);
    warpedRef.current = null;
    stageFrameLockedRef.current = null;
    setStageFrameH(null);
    if (page.originalFile && isInvoiceImageFile(page.originalFile) && !isInvoicePdfFile(page.originalFile)) {
      importOriginalRef.current = page.originalFile;
      setWorkingFile(page.originalFile);
      // Show saved straighten immediately; Back to corners reloads the original.
      if (page.previewUrl) {
        setResult({ file: page.file, url: page.previewUrl, owned: false });
      } else {
        setResult(null);
      }
    } else if (page.previewUrl) {
      setResult({ file: page.file, url: page.previewUrl, owned: false });
    }
  }

  function removeDraftPage(idx) {
    const page = draftPages[idx];
    if (!page || busy) return;
    if (page.previewUrl) URL.revokeObjectURL(page.previewUrl);
    const next = draftPages.filter((_, i) => i !== idx);
    setDraftPages(next);
    if (editingIndex === idx) {
      setEditingIndex(null);
      setResult(null);
      setAwaitingNextPage(next.length > 0);
    } else if (editingIndex != null && editingIndex > idx) {
      setEditingIndex(editingIndex - 1);
    }
  }

  function moveDraftPage(idx, dir) {
    const j = idx + dir;
    if (j < 0 || j >= draftPages.length) return;
    const next = draftPages.slice();
    const [item] = next.splice(idx, 1);
    next.splice(j, 0, item);
    setDraftPages(next);
    if (editingIndex === idx) setEditingIndex(j);
    else if (editingIndex === j) setEditingIndex(idx);
  }

  function startAddPageFromSidebar() {
    if (busy) return;
    if (result?.file) {
      saveResultToDrafts();
      setResult(null);
      setResultTurns(0);
      warpedRef.current = null;
    }
    setEditingIndex(null);
    setAwaitingNextPage(true);
    setError(null);
  }

  const points =
    layout && corners
      ? corners.map((c) => ({
          x: layout.offsetX + c.x * layout.scale,
          y: layout.offsetY + c.y * layout.scale,
        }))
      : null;

  let loupeStyle = null;
  if (drag && points && layout && imgRef.current) {
    let p = null;
    let preferBelow = false;
    if (drag.type === "corner" && points[drag.index]) {
      p = points[drag.index];
      preferBelow = drag.index === 0 || drag.index === 1;
    } else if (drag.type === "edge" && points[drag.i0] && points[drag.i1]) {
      p = {
        x: (points[drag.i0].x + points[drag.i1].x) / 2,
        y: (points[drag.i0].y + points[drag.i1].y) / 2,
      };
      // Top edge (0–1) prefers loupe below so it doesn't cover the side.
      preferBelow = drag.i0 === 0 && drag.i1 === 1;
    }
    if (p) {
      const imgRect = imgRef.current.getBoundingClientRect();
      // Fixed coords — may sit outside the photo. Mobile/touch: always above the finger.
      // Desktop/mouse: under for top anchors / top edge, above for bottom.
      const screenX = imgRect.left + p.x;
      const screenY = imgRect.top + p.y;
      const aboveTop = screenY - LOUPE_SIZE - LOUPE_GAP;
      const belowTop = screenY + LOUPE_GAP;
      let top;
      if (dragIsTouch) {
        top = aboveTop;
      } else {
        top = preferBelow || aboveTop < 8 ? belowTop : aboveTop;
      }
      loupeStyle = {
        position: "fixed",
        left: screenX - LOUPE_SIZE / 2,
        top,
        width: LOUPE_SIZE,
        height: LOUPE_SIZE,
        borderRadius: "50%",
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 1400,
        boxShadow: "0 8px 24px rgba(0,0,0,0.35), 0 0 0 2px rgba(45,106,79,0.35)",
      };
    }
  }


  const pageCount = draftPages.length;
  const nextPageNum = pageCount + 1;
  const title = awaitingNextPage
    ? t(`Add page ${nextPageNum}`, `Προσθήκη σελίδας ${nextPageNum}`)
    : result
      ? editingIndex != null
        ? t(`Check page ${editingIndex + 1}`, `Έλεγχος σελίδας ${editingIndex + 1}`)
        : pageCount > 0
          ? t(`Check page ${pageCount + 1}`, `Έλεγχος σελίδας ${pageCount + 1}`)
          : t("Check the result", "Έλεγχος αποτελέσματος")
      : pageCount > 0 || editingIndex != null
        ? t(
            `Straighten page ${editingIndex != null ? editingIndex + 1 : pageCount + 1}`,
            `Ίσιωμα σελίδας ${editingIndex != null ? editingIndex + 1 : pageCount + 1}`
          )
        : t("Straighten invoice photo", "Ίσιωμα φωτο τιμολογίου");

  const stageBoxStyle = {
    position: "relative",
    width: "100%",
    height: stageFrameH != null ? stageFrameH : "min(72vh, 720px)",
    maxHeight: stageFrameH != null ? stageFrameH : "min(72vh, 720px)",
    flex: "0 0 auto",
    background: "#111",
    borderRadius: 12,
    border: "1px solid var(--border)",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
  };

  const sidebar = (
    <aside
      style={{
        width: 104,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        gap: 6,
        overflowY: "auto",
        maxHeight: stageFrameH != null ? stageFrameH : "min(72vh, 720px)",
        paddingRight: 2,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          color: "var(--text-muted)",
          textTransform: "uppercase",
          letterSpacing: 0.3,
          padding: "0 2px",
        }}
      >
        {t("Pages", "Σελίδες")}
      </div>
      {draftPages.map((page, pIdx) => {
        const selected =
          !awaitingNextPage &&
          (editingIndex === pIdx || (editingIndex == null && result && pIdx === draftPages.length - 1));
        return (
          <div
            key={page.id}
            style={{
              position: "relative",
              borderRadius: 8,
              border: selected
                ? "2px solid var(--green)"
                : "1px solid var(--border, #e5e7eb)",
              background: "var(--surface, #fff)",
              overflow: "hidden",
            }}
          >
            <button
              type="button"
              disabled={busy}
              onClick={() => selectDraftPage(pIdx)}
              style={{
                display: "block",
                width: "100%",
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: busy ? "default" : "pointer",
              }}
              title={t(`Page ${pIdx + 1}`, `Σελίδα ${pIdx + 1}`)}
            >
              {page.previewUrl ? (
                <img
                  src={page.previewUrl}
                  alt=""
                  style={{
                    width: "100%",
                    height: 72,
                    objectFit: "cover",
                    display: "block",
                    background: "var(--surface)",
                  }}
                />
              ) : (
                <div
                  style={{
                    height: 72,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--text-muted)",
                  }}
                >
                  PDF
                </div>
              )}
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  padding: "3px 5px",
                  color: selected ? "var(--green)" : "var(--text-primary)",
                }}
              >
                {t(`Page ${pIdx + 1}`, `Σελ. ${pIdx + 1}`)}
              </div>
            </button>
            <div
              style={{
                display: "flex",
                gap: 2,
                padding: "0 4px 4px",
                justifyContent: "space-between",
              }}
            >
              <button
                type="button"
                className="btn-base btn-outline btn-small"
                disabled={busy || pIdx === 0}
                onClick={() => moveDraftPage(pIdx, -1)}
                title={t("Move up", "Πάνω")}
                style={{ padding: "2px 6px", fontSize: 11, minWidth: 0 }}
              >
                ↑
              </button>
              <button
                type="button"
                className="btn-base btn-outline btn-small"
                disabled={busy || pIdx >= draftPages.length - 1}
                onClick={() => moveDraftPage(pIdx, 1)}
                title={t("Move down", "Κάτω")}
                style={{ padding: "2px 6px", fontSize: 11, minWidth: 0 }}
              >
                ↓
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => removeDraftPage(pIdx)}
                aria-label={t("Remove page", "Αφαίρεση σελίδας")}
                title={t("Remove page", "Αφαίρεση σελίδας")}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "var(--text-muted)",
                  cursor: busy ? "default" : "pointer",
                  fontSize: 14,
                  lineHeight: 1,
                  padding: "2px 4px",
                }}
              >
                ×
              </button>
            </div>
          </div>
        );
      })}
      <button
        type="button"
        className="btn-base btn-outline btn-small"
        disabled={busy}
        onClick={startAddPageFromSidebar}
        style={{
          width: "100%",
          justifyContent: "center",
          padding: "8px 6px",
          fontSize: 11,
          borderStyle: awaitingNextPage ? "solid" : "dashed",
          borderColor: awaitingNextPage ? "var(--green)" : undefined,
        }}
      >
        + {t("Add Page", "Προσθήκη σελίδας")}
      </button>
    </aside>
  );

  let mainBody;
  if (awaitingNextPage) {
    mainBody = (
      <div
        className="photo-upload-zone"
        role="button"
        tabIndex={0}
        style={{
          ...stageBoxStyle,
          background: nextDragOver ? "var(--green-light)" : "var(--surface2)",
          borderColor: nextDragOver ? "var(--green)" : "var(--border)",
          borderStyle: "dashed",
          cursor: "pointer",
          flexDirection: "column",
          gap: 8,
          padding: 16,
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setNextDragOver(true);
        }}
        onDragLeave={() => setNextDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          acceptNextPageFile(e.dataTransfer.files?.[0]);
        }}
        onClick={async () => {
          const next = await pickInvoiceUploadFile();
          if (next) acceptNextPageFile(next);
        }}
        onKeyDown={async (e) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          e.preventDefault();
          const next = await pickInvoiceUploadFile();
          if (next) acceptNextPageFile(next);
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 800, color: "var(--text-primary)", textAlign: "center" }}>
          {t("Drop page photo here", "Σύρετε τη φωτο σελίδας εδώ")}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "center" }}>
          {pageCount === 0
            ? t("or tap to choose a file", "ή πατήστε για επιλογή αρχείου")
            : t(
                `${pageCount} page${pageCount === 1 ? "" : "s"} saved — add the next or tap Done`,
                `${pageCount} σελίδ${pageCount === 1 ? "α" : "ες"} αποθηκεύτηκαν — προσθέστε την επόμενη ή πατήστε Τέλος`
              )}
        </div>
      </div>
    );
  } else if (result) {
    mainBody = (
      <div ref={resultStageRef} style={stageBoxStyle}>
        <img
          src={result.url}
          alt={t("Straightened invoice", "Ισιωμένο τιμολόγιο")}
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "contain",
            pointerEvents: "none",
          }}
        />
      </div>
    );
  } else {
    mainBody = (
      <div
        ref={stageRef}
        style={{
          ...stageBoxStyle,
          background: "var(--surface2)",
          touchAction: "none",
          userSelect: "none",
        }}
      >
        {objectUrl ? (
          <img
            ref={imgRef}
            src={objectUrl}
            alt=""
            onLoad={measure}
            draggable={false}
            style={{
              display: "block",
              width: "100%",
              height: "100%",
              objectFit: "contain",
              pointerEvents: "none",
            }}
          />
        ) : (
          <div style={{ padding: 48, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
            {t("Loading photo…", "Φόρτωση φωτο…")}
          </div>
        )}
        {points && (
          <svg
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
            aria-hidden
          >
            <polygon
              points={points.map((p) => `${p.x},${p.y}`).join(" ")}
              fill="rgba(45, 106, 79, 0.18)"
              stroke="var(--green)"
              strokeWidth="2"
            />
            {EDGE_PAIRS.map(({ i0, i1, axis }, ei) => {
              const a = points[i0];
              const b = points[i1];
              const mx = (a.x + b.x) / 2;
              const my = (a.y + b.y) / 2;
              const active =
                drag?.type === "edge" && drag.i0 === i0 && drag.i1 === i1;
              const startEdge = (e, isTouch) => {
                e.preventDefault();
                e.stopPropagation();
                if (!layout || !corners) return;
                const img = imgRef.current;
                if (!img) return;
                const clientX = e.touches ? e.touches[0].clientX : e.clientX;
                const clientY = e.touches ? e.touches[0].clientY : e.clientY;
                setDragIsTouch(isTouch || e.pointerType === "touch");
                setDrag({
                  type: "edge",
                  i0,
                  i1,
                  axis,
                  origin: clientToNatural(clientX, clientY, img, layout, natural),
                  base: [
                    { x: corners[i0].x, y: corners[i0].y },
                    { x: corners[i1].x, y: corners[i1].y },
                  ],
                });
              };
              const pill =
                axis === "y"
                  ? { x: mx - 11, y: my - 6, w: 22, h: 12 }
                  : { x: mx - 6, y: my - 11, w: 12, h: 22 };
              return (
                <g key={`edge-${ei}`} opacity={active ? 0.55 : 1}>
                  <line
                    x1={a.x}
                    y1={a.y}
                    x2={b.x}
                    y2={b.y}
                    stroke="transparent"
                    strokeWidth="28"
                    strokeLinecap="round"
                    style={{
                      cursor: axis === "y" ? "ns-resize" : "ew-resize",
                      touchAction: "none",
                    }}
                    onPointerDown={(e) => startEdge(e, false)}
                    onTouchStart={(e) => startEdge(e, true)}
                  />
                  <rect
                    x={pill.x}
                    y={pill.y}
                    width={pill.w}
                    height={pill.h}
                    rx={4}
                    fill="#fff"
                    stroke="var(--green)"
                    strokeWidth="2.5"
                    style={{ pointerEvents: "none" }}
                  />
                </g>
              );
            })}
            {points.map((p, i) => (
              <g
                key={HANDLE_LABELS[i]}
                opacity={drag?.type === "corner" && drag.index === i ? 0.35 : 1}
              >
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={18}
                  fill="transparent"
                  style={{ cursor: "grab", touchAction: "none" }}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragIsTouch(e.pointerType === "touch");
                    setDrag({ type: "corner", index: i });
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragIsTouch(true);
                    setDrag({ type: "corner", index: i });
                  }}
                />
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={14}
                  fill="#fff"
                  stroke="var(--green)"
                  strokeWidth="3"
                  style={{ pointerEvents: "none" }}
                />
                <text
                  x={p.x}
                  y={p.y + 4}
                  textAnchor="middle"
                  fontSize="9"
                  fontWeight="800"
                  fill="var(--green)"
                  style={{ pointerEvents: "none" }}
                >
                  {i + 1}
                </text>
              </g>
            ))}
          </svg>
        )}
      </div>
    );
  }

  let footer;
  if (awaitingNextPage) {
    footer = (
      <>
        <button
          type="button"
          className="btn-base btn-green"
          disabled={busy || pageCount === 0}
          onClick={finishWithoutMorePages}
        >
          {t("Done", "Τέλος")}
        </button>
        <button type="button" className="btn-base btn-outline" disabled={busy} onClick={onCancel}>
          {t("Cancel", "Ακύρωση")}
        </button>
      </>
    );
  } else if (result) {
    footer = (
      <>
        <button
          type="button"
          className="btn-base btn-outline"
          disabled={busy}
          onClick={() => handleRotateResult(1)}
          aria-label={t("Rotate 90° right", "Περιστροφή 90° δεξιά")}
          title={t("Rotate 90° right", "Περιστροφή 90° δεξιά")}
        >
          ↻ {t("Rotate", "Περιστροφή")}
        </button>
        <button
          type="button"
          className="btn-base btn-outline"
          disabled={busy}
          onClick={() => handleRotateResult(3)}
          aria-label={t("Rotate 90° left", "Περιστροφή 90° αριστερά")}
          title={t("Rotate 90° left", "Περιστροφή 90° αριστερά")}
        >
          ↺
        </button>
        <button
          type="button"
          className={`btn-base ${enhance ? "btn-green" : "btn-outline"}`}
          disabled={busy || !result.owned}
          onClick={handleToggleEnhance}
          aria-pressed={enhance}
          title={t(
            "White paper, crisp ink — like a scanner app",
            "Λευκό χαρτί, καθαρά γράμματα — σαν εφαρμογή σάρωσης"
          )}
          style={{ opacity: enhance ? 1 : 0.85 }}
        >
          ✦ {enhance ? t("Enhanced", "Βελτιωμένο") : t("Enhance", "Βελτίωση")}
        </button>
        <button
          type="button"
          className="btn-base btn-green"
          disabled={busy}
          onClick={() => handleUseResult()}
        >
          {busy
            ? t("Working…", "Επεξεργασία…")
            : pageCount > 0 || editingIndex != null
              ? t("Use all pages", "Χρήση όλων των σελίδων")
              : t("Use this", "Χρήση")}
        </button>
        <button
          type="button"
          className="btn-base btn-outline"
          disabled={busy}
          onClick={() => handleUseResult({ addAnother: true })}
          title={t(
            "Save this page, then pick the next page of the invoice",
            "Αποθήκευση σελίδας και επιλογή της επόμενης"
          )}
        >
          {t("Add Page", "Προσθήκη σελίδας")}
        </button>
        <button
          type="button"
          className="btn-base btn-outline"
          disabled={busy}
          onClick={() => {
            setResult(null);
            setError(null);
          }}
        >
          {t("Back to corners", "Πίσω στις γωνίες")}
        </button>
        <button type="button" className="btn-base btn-outline" disabled={busy} onClick={onCancel}>
          {t("Cancel", "Ακύρωση")}
        </button>
      </>
    );
  } else {
    footer = (
      <>
        <button
          type="button"
          className="btn-base btn-outline"
          disabled={busy || !objectUrl}
          onClick={() => handleRotate(1)}
          aria-label={t("Rotate 90° right", "Περιστροφή 90° δεξιά")}
          title={t("Rotate 90° right", "Περιστροφή 90° δεξιά")}
        >
          ↻ {t("Rotate", "Περιστροφή")}
        </button>
        <button
          type="button"
          className="btn-base btn-outline"
          disabled={busy || !objectUrl}
          onClick={() => handleRotate(3)}
          aria-label={t("Rotate 90° left", "Περιστροφή 90° αριστερά")}
          title={t("Rotate 90° left", "Περιστροφή 90° αριστερά")}
        >
          ↺
        </button>
        <button
          type="button"
          className="btn-base btn-green"
          disabled={busy || !corners}
          onClick={handleStraighten}
        >
          {busy ? t("Working…", "Επεξεργασία…") : t("Straighten", "Ίσιωμα")}
        </button>
        <button
          type="button"
          className="btn-base btn-outline"
          disabled={busy || detectBusy || !objectUrl}
          onClick={handleDetectEdges}
        >
          {detectBusy
            ? t("Detecting…", "Εντοπισμός…")
            : t("Detect edges", "Εντοπισμός άκρων")}
        </button>
        <button
          type="button"
          className="btn-base btn-outline"
          disabled={busy || detectBusy}
          onClick={() => {
            if (!natural.w) return;
            setCorners(defaultCorners(natural.w, natural.h));
            setDetectNote("fallback");
            setError(null);
          }}
        >
          {t("Reset corners", "Επαναφορά γωνιών")}
        </button>
        {pageCount > 0 ? (
          <button
            type="button"
            className="btn-base btn-outline"
            disabled={busy}
            onClick={startAddPageFromSidebar}
          >
            {t("Add Page", "Προσθήκη σελίδας")}
          </button>
        ) : null}
        <button type="button" className="btn-base btn-outline" disabled={busy} onClick={onCancel}>
          {t("Cancel", "Ακύρωση")}
        </button>
      </>
    );
  }

  return (
    <div className="modal-overlay" style={{ zIndex: 1200 }}>
      <div
        className="card-box"
        style={{
          maxWidth: 960,
          width: "96%",
          maxHeight: "96vh",
          margin: "16px auto",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <div style={{ flexShrink: 0, marginBottom: 8, minHeight: result || awaitingNextPage ? undefined : 72 }}>
          <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4 }}>{title}</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            {awaitingNextPage
              ? t(
                  "Drop the next page, or tap Done when the invoice is complete.",
                  "Σύρετε την επόμενη σελίδα, ή πατήστε Τέλος όταν ολοκληρώσετε."
                )
              : result
                ? t(
                    "We rotated the page so the text reads upright. If it is still sideways or upside down, rotate it here, then use it.",
                    "Περιστρέψαμε τη σελίδα ώστε το κείμενο να διαβάζεται όρθιο. Αν είναι ακόμα πλάγιο ή ανάποδο, περιστρέψτε το εδώ και μετά χρησιμοποιήστε το."
                  )
                : (
                  <>
                    {t(
                      "We try to find the page automatically when contrast is clear. Rotate if sideways, then fine-tune corners or drag a whole edge — a magnifier shows the drop point.",
                      "Προσπαθούμε να βρούμε τη σελίδα αυτόματα όταν υπάρχει καθαρή αντίθεση. Περιστρέψτε αν είναι πλάγια και διορθώστε γωνίες ή σύρετε ολόκληρη πλευρά — μεγεθυντής δείχνει το σημείο."
                    )}
                    {detectNote === "auto" && (
                      <span style={{ display: "block", marginTop: 4, color: "var(--green)", fontWeight: 600 }}>
                        {t(
                          "Page found — adjust corners or edges if needed.",
                          "Βρέθηκε σελίδα — διορθώστε γωνίες ή πλευρές αν χρειάζεται."
                        )}
                        <span style={{ marginLeft: 6, opacity: 0.65, fontWeight: 500, fontSize: 11 }}>
                          ({PAGE_DETECT_VERSION})
                        </span>
                      </span>
                    )}
                    {detectNote === "fallback" && (
                      <span style={{ display: "block", marginTop: 4 }}>
                        {t(
                          "No clear page found — drag the corners onto the paper.",
                          "Δεν βρέθηκε καθαρή σελίδα — σύρετε τις γωνίες πάνω στο χαρτί."
                        )}
                      </span>
                    )}
                  </>
                )}
          </div>
          {error && (
            <div style={{ color: "var(--danger, #b91c1c)", fontSize: 12, fontWeight: 700, marginTop: 8 }}>
              {error}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "stretch",
            flexShrink: 0,
          }}
        >
          {sidebar}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {mainBody}
          </div>
        </div>

        <div
          style={
            loupeStyle || {
              position: "fixed",
              left: -9999,
              top: -9999,
              width: LOUPE_SIZE,
              height: LOUPE_SIZE,
              visibility: "hidden",
              pointerEvents: "none",
              zIndex: 1400,
            }
          }
          aria-hidden
        >
          <canvas ref={loupeRef} />
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            marginTop: 12,
            flexShrink: 0,
          }}
        >
          {footer}
        </div>
      </div>
    </div>
  );
}
